import { Link, useLocation } from "react-router-dom";
import { findActiveItem } from "@/components/shell/nav-match";
import type { IconName } from "@/lib/icons";
import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";

export interface LinkTab {
  key: string;
  label: string;
  href: string;
  icon?: IconName;
  /** Other URLs on which this tab is the current one. */
  match?: string[];
}

/**
 * Sub-views of one menu entry, as links (so each has its own URL and the back
 * button works). Replaces the old accordion children of the sidebar.
 */
export function LinkTabs({ tabs, label, className }: { tabs: LinkTab[]; label: string; className?: string }) {
  const location = useLocation();
  const active = findActiveItem(
    [{ key: "tabs", items: tabs.map((tab) => ({ ...tab, icon: tab.icon ?? "fileText" })) }],
    location.pathname,
    location.search,
  );
  return (
    <nav aria-label={label} className={cn("-mx-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden", className)}>
      <ul className="flex min-w-max gap-1 border-b border-border/60 px-1">
        {tabs.map((tab) => {
          const current = tab.key === active?.key;
          return (
            <li key={tab.key}>
              <Link
                to={tab.href}
                aria-current={current ? "page" : undefined}
                className={cn(
                  "relative flex h-10 items-center gap-2 px-3 text-[13px] transition-colors",
                  current ? "font-medium text-foreground" : "text-muted-foreground hover:text-foreground",
                  current && "after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full after:bg-primary",
                )}
              >
                {tab.icon ? <Icon name={tab.icon} className="size-4" /> : null}
                {tab.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
